'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Instagram, Facebook, ChevronDown, ChevronUp, ImageIcon, Video, Reply, ExternalLink, EyeOff, Eye, Trash2, DollarSign, Linkedin, Heart, Youtube } from 'lucide-react';
import { UIComment, UIPost, CommentType } from 'src/types/social';
import logger from '@/lib/logger';

interface PostCardProps {
  post: UIPost;
  onGenerateAIReply: (comment: UIComment, postContext?: string, style?: string) => Promise<string>;
  onSubmitReply: (commentId: string, replyText: string) => Promise<void>;
  // Reverted: Prop for non-YouTube platforms requires boolean
  onToggleCommentVisibility?: (commentId: string, hidden: boolean) => Promise<void>; 
  // New prop specifically for initiating YouTube hide flow
  onInitiateHideYoutubeComment?: (commentId: string) => void; 
  onDeleteComment?: (commentId: string) => Promise<void>;
  onToggleLike?: (commentId: string, hasLiked: boolean) => Promise<void>;
  showHiddenComments: boolean; // Prop for hidden comments toggle
  showRepliedComments: boolean; // Add prop for replied comments toggle
}

export default function PostCard({
  post, 
  onGenerateAIReply, 
  onSubmitReply, 
  onToggleCommentVisibility,
  onInitiateHideYoutubeComment, // Receive new prop
  onDeleteComment,
  onToggleLike,
  showHiddenComments, // Receive hidden prop
  showRepliedComments // Receive replied prop
}: PostCardProps) {
  // Define moderation options based on comment type
  const moderationOptions: Record<CommentType, ('like' | 'hide' | 'delete')[]> = {
    ig_org_ig: ['hide', 'delete'],
    ig_org_fb: ['hide', 'delete'],
    fb_org: ['hide', 'delete'],
    ig_ad: ['hide', 'delete'],
    fb_ad: ['hide', 'delete'],
    linkedin: ['like', 'delete'],
    youtube: ['hide', 'delete'], // Keep 'hide' as the action type
  };

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [error, setError] = useState('');
  const [removingCommentId, setRemovingCommentId] = useState<string | null>(null);
  const [shouldHideCard, setShouldHideCard] = useState(false); 
  const [togglingVisibility, setTogglingVisibility] = useState<string | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [togglingLikeId, setTogglingLikeId] = useState<string | null>(null);
  const [intendedDisplayCount, setIntendedDisplayCount] = useState(3); 

   // Sort comments by timestamp in descending order (newest first)
  const sortedComments = [...(Array.isArray(post.comments) ? post.comments : [])]
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  // Calculate the comments that *should* be displayed based on current filters
  // Reverted: No need to check moderationStatus as rejected comments aren't fetched for YouTube
  const displayableComments = sortedComments.filter(comment =>
    (showHiddenComments || !comment.hidden) && 
    (showRepliedComments || !(comment.hasBeenRepliedTo === true)) 
  );

  // Slice the displayable comments based on the intended count
  const displayedComments = displayableComments.slice(0, intendedDisplayCount);

  // Effect to control card visibility based on displayable comments
  useEffect(() => {
    if (displayableComments.length === 0) {
      setShouldHideCard(true);
    } else if (shouldHideCard) {
      setShouldHideCard(false);
    }
  }, [displayableComments.length, shouldHideCard]); 

  const [replyStyle, setReplyStyle] = useState(() => {
    const saved = localStorage.getItem('replyStyle');
    return saved || 'professional';
  });

  const handleGenerateAIReply = async (comment: UIComment) => {
    setGeneratingAI(true);
    setError('');
    try {
      const aiReply = await onGenerateAIReply(comment, post.caption || undefined, replyStyle);
      setReplyText(aiReply);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate AI reply');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSubmitReply = async (commentId: string) => {
    setError('');
    setIsPosting(true);
    try {
      setRemovingCommentId(commentId);
      await onSubmitReply(commentId, replyText);
      await new Promise(resolve => setTimeout(resolve, 300));
      setReplyingTo(null);
      setReplyText('');
      setRemovingCommentId(null); 
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit reply');
      setRemovingCommentId(null); 
    } finally {
      setIsPosting(false); 
    }
  };

  // Reverted: Original function for non-YouTube platforms
  const handleToggleVisibility = async (commentId: string, currentHidden: boolean) => { 
    if (onToggleCommentVisibility) {
      setTogglingVisibility(commentId);
      try {
        await onToggleCommentVisibility(commentId, !currentHidden); 
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to toggle comment visibility');
      } finally {
        setTogglingVisibility(null);
      }
    }
  };

  const handleToggleLike = async (commentId: string, currentHasLiked: boolean) => {
    if (onToggleLike) {
      setTogglingLikeId(commentId);
      try {
        await onToggleLike(commentId, !currentHasLiked);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to toggle like');
      } finally {
        setTogglingLikeId(null);
      }
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (onDeleteComment) {
      setDeletingCommentId(commentId);
      try {
        setRemovingCommentId(commentId);
        await onDeleteComment(commentId);
        await new Promise(resolve => setTimeout(resolve, 300));
        setRemovingCommentId(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete comment');
        setRemovingCommentId(null); 
      } finally {
        setDeletingCommentId(null);
      }
    }
  };

  const loadMoreComments = () => {
    setIntendedDisplayCount(prevCount => Math.min(displayableComments.length, prevCount + 3)); 
  };

  const hasMoreComments = displayedComments.length < displayableComments.length;

  const getAvatarText = (username: string) => {
    if (!username || typeof username !== 'string' || username.length === 0) {
      return '?';
    }
    return username[0].toUpperCase();
  };

  const mediaType = post.media_type || post.media?.media_type;
  const mediaUrl = mediaType === 'VIDEO' ? post.thumbnail_url : post.media_url;
  const videoSource = post.permalink;

  return (
    <Card className={`mb-4 transition-all duration-300 transform ${shouldHideCard ? 'opacity-0 scale-95 h-0 !mb-0 overflow-hidden pointer-events-none' : 'opacity-100 scale-100'}`}>
      <CardHeader className="py-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {post.platform === 'instagram' && <Instagram className="h-4 w-4" />}
            {post.platform === 'facebook' && <Facebook className="h-4 w-4" />}
            {post.platform === 'ads' && <DollarSign className="h-4 w-4" />}
            {post.platform === 'linkedin' && <Linkedin className="h-4 w-4" />}
            {post.platform === 'youtube' && <Youtube className="h-4 w-4" />}
            <span className="text-sm font-medium">@{post.username}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? "Expand post" : "Collapse post"}
          >
            {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </Button>
        </CardTitle>
        <p className="text-sm mt-2">
          {isCollapsed ? `${post.caption.slice(0, 100)}...` : post.caption}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          {new Date(post.timestamp).toLocaleDateString()}
        </p>
      </CardHeader>
      {!isCollapsed && (
        <CardContent className="py-2">
          {/* Media Section */}
          {mediaUrl && (
            <div className="mb-8"> 
              {mediaType === 'VIDEO' ? (
                <div className="inline-block">
                  <div className="relative h-[200px] w-auto bg-gray-200 rounded-md group">
                    <Image 
                      src={mediaUrl} 
                      alt="Video thumbnail" 
                      height={200}
                      width={355} // Added width for better layout calculation
                      className="h-[200px] w-auto object-contain object-left rounded-md"
                      priority
                      unoptimized={post.platform === 'youtube'} 
                      onError={(e) => {
                        logger.error(`Error loading thumbnail for ${post.id}:`, e);
                        const imgElement = e.target as HTMLImageElement;
                        imgElement.src = '/video-placeholder.png';
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Video className="h-8 w-8 text-white" />
                    </div>
                    {videoSource && (
                      <a 
                        href={videoSource}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute bottom-1 right-1 bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-75 transition-opacity"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="inline-block">
                  <Image 
                    src={mediaUrl} 
                    alt="Post media" 
                    height={200}
                    width={200} // Added width for better layout calculation
                    className="h-[200px] w-auto object-contain object-left rounded-md"
                    priority
                  />
                  <div className="relative">
                    <ImageIcon className="absolute bottom-1 right-1 h-4 w-4 text-white bg-black bg-opacity-50 rounded-full p-1" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Comments Section */}
          <div className="space-y-4">
            {displayedComments.map(comment => {
              const isYouTube = comment.commentType === 'youtube';
              // Reverted: isHidden check uses comment.hidden only
              const isHidden = !!comment.hidden; 
              const repliedStyle = showRepliedComments && comment.hasBeenRepliedTo ? 'opacity-60' : '';
              
              return (
              <div 
                key={comment.id} 
                className={`pb-4 border-b last:border-b-0 transition-all duration-300 transform ${repliedStyle} ${
                  removingCommentId === comment.id ? 'opacity-0 scale-95 -translate-x-2 h-0 overflow-hidden' : 'opacity-100 scale-100 translate-x-0'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback>{getAvatarText(comment.username)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <span className="text-sm font-semibold">{comment.username || 'Unknown User'}</span>
                      <p className="text-xs text-gray-500">
                        {new Date(comment.timestamp).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    {moderationOptions[comment.commentType]?.map(option => {
                      if (option === 'like' && onToggleLike) {
                        return (
                          <Button
                            key="like"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleLike(comment.id, !!comment.hasLiked)}
                            disabled={togglingLikeId === comment.id}
                            className={`${comment.hasLiked ? 'text-primary' : ''} flex gap-1 items-center`}
                          >
                            <Heart className={`h-4 w-4 ${comment.hasLiked ? 'fill-current' : ''}`} />
                            {comment.likes || 0}
                          </Button>
                        );
                      }
                      // Check for the correct handler based on platform
                      if (option === 'hide' && (onToggleCommentVisibility || onInitiateHideYoutubeComment)) { 
                        return (
                          <Button
                            key="hide"
                            variant="ghost"
                            size="sm"
                            // Updated onClick logic: Use new handler for YouTube, old one otherwise
                            onClick={() => {
                              if (isYouTube && onInitiateHideYoutubeComment) {
                                onInitiateHideYoutubeComment(comment.id);
                              } else if (!isYouTube && onToggleCommentVisibility) {
                                // Pass the current hidden state for non-YouTube
                                handleToggleVisibility(comment.id, !!comment.hidden); 
                              }
                            }}
                            disabled={togglingVisibility === comment.id}
                            // Reverted: Style based on 'hidden' prop only
                            className={comment.hidden ? 'text-gray-400' : ''} 
                          >
                            {/* Reverted: Icon based on 'hidden' prop only for non-YouTube. YouTube always shows Eye. */}
                            {!isYouTube && comment.hidden ? ( 
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" /> // Always show Eye for YouTube hide action
                            )}
                          </Button>
                        );
                      }
                      if (option === 'delete' && onDeleteComment) {
                        return (
                          <Button
                            key="delete"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteComment(comment.id)}
                            disabled={deletingCommentId === comment.id}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
                {/* Reverted: Style based on 'hidden' prop only */}
                <p className={`text-sm mb-2 ${comment.hidden ? 'text-gray-400' : ''}`}>{comment.text}</p> 
                {/* Reverted: Show reply controls if not hidden */}
                {!comment.hidden && replyingTo === comment.id ? ( 
                  <>
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      className="mb-2 text-sm"
                      placeholder="Write your reply..."
                    />
                    {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Select
                          value={replyStyle}
                          onValueChange={(value) => {
                            setReplyStyle(value);
                            localStorage.setItem('replyStyle', value);
                          }}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue placeholder="Reply style" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="professional">Professional</SelectItem>
                            <SelectItem value="friendly">Friendly</SelectItem>
                            <SelectItem value="bold">Bold</SelectItem>
                            <SelectItem value="trendy">Gen-Z Slang</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleGenerateAIReply(comment)}
                          disabled={generatingAI || isPosting}
                        >
                          {generatingAI ? 'Generating...' : 'Generate AI Reply'}
                        </Button>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleSubmitReply(comment.id)}
                          disabled={!replyText.trim() || isPosting}
                        >
                          {isPosting ? 'Posting...' : 'Send Response'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyText('');
                            setError('');
                          }}
                          disabled={isPosting}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </>
                ) : !comment.hidden && ( // Reverted: Show reply button if not hidden (Fixed syntax error here)
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setReplyingTo(comment.id);
                      setReplyText('');
                      setError('');
                    }}
                    className="hover:bg-blue-600 hover:text-white transition-colors flex gap-1 items-center"
                  >
                    <Reply className="h-4 w-4" />
                    Reply
                  </Button>
                )}
              </div>
            );
          })}
          </div>

          {hasMoreComments && (
            <Button
              onClick={loadMoreComments}
              variant="outline"
              className="w-full mt-4"
            >
              Load More Comments
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  );
}
